import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "./axiosBaseQuery";
import type {
  CreditAccount,
  CreditHistoryPage,
  CreditHistoryQuery,
} from "@/lib/types/credit.types";

/**
 * Credit ledger API.
 *
 * Unlike the scaffold APIs built on `createBaseApi` (which still use the mock
 * base query), this one talks to the real Express middleware at
 * `/api/credit/*`. The middleware resolves the user from the session cookie
 * and forwards to the Credit Management service, so no user id is sent from
 * the browser — the session cookie carries it instead.
 *
 * Routed through `axiosBaseQuery` so these calls share the session handling in
 * lib/axios: a expired access token is refreshed and retried, and a disabled
 * account signs out once instead of failing silently on every request.
 */

interface Envelope<T> {
  success: boolean;
  message?: string;
  data: T;
}

const EMPTY_PAGE: CreditHistoryPage = {
  items: [],
  page: 1,
  limit: 10,
  hasMore: false,
};

export const creditApi = createApi({
  reducerPath: "creditApi",
  baseQuery: axiosBaseQuery("/api/credit"),
  tagTypes: ["CreditBalance", "CreditHistory"],
  // Not refetchOnFocus: the balance badge stays mounted while signed in, and
  // the credit guard lazily refreshes the balance before every spend, so focus
  // events would only add noise.
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    /**
     * Balance for the signed-in user. Kept briefly cached so the top-bar
     * badge and the usage page share a single request.
     */
    getCreditBalance: builder.query<CreditAccount | null, void>({
      query: () => "/balance",
      transformResponse: (response: Envelope<CreditAccount | null>) =>
        response?.data ?? null,
      providesTags: ["CreditBalance"],
      keepUnusedDataFor: 60,
    }),

    /** Paginated ledger for the signed-in user, newest first. */
    getCreditHistory: builder.query<CreditHistoryPage, CreditHistoryQuery | void>({
      query: (args) => {
        const { page = 1, limit = 10, direction, category, status } = args ?? {};
        return {
          url: "/history",
          params: {
            page,
            limit,
            ...(direction ? { direction } : {}),
            ...(category ? { category } : {}),
            ...(status ? { status } : {}),
          },
        };
      },
      transformResponse: (response: Envelope<CreditHistoryPage>) =>
        response?.data ?? EMPTY_PAGE,
      providesTags: ["CreditHistory"],
      keepUnusedDataFor: 60,
    }),
  }),
});

export const {
  useGetCreditBalanceQuery,
  useGetCreditHistoryQuery,
  useLazyGetCreditHistoryQuery,
} = creditApi;

export default creditApi;
